import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, RefreshCw } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { DEFAVORABLE, FAVORABLE, NEUTRE_DOUX } from '../../lib/couleurs';
import {
  enregistrerReglementCanonique,
  fetchBanqueCanonique,
  fetchDepensesCanoniques,
  fetchFinancementsCanoniques,
  fetchFournisseursCanoniques,
  fetchPrevisionnelCanonique,
  fetchRetenuesCanoniques,
  saisirFactureCanonique,
} from '../../lib/passerelle';
import { Carte, btn, btnLight, inputCls, labelCls, td, th } from '../cri/ui';

// Source de vérité unique par catégorie : ces écrans LISENT le canonique serveur
// (collections de la passerelle) et y ÉCRIVENT directement. Aucune sauvegarde de
// `db.store` ne peut donc écraser une opération de la passerelle.
const SOURCES = {
  depenses: 'business_documents + obligations (serveur)',
  fournisseurs: 'obligations + payments + settlement_allocations (serveur)',
  banque: 'bank_statements / bank_transactions / bank_matches (serveur)',
  previsionnel: 'obligations (hors contrats) + schedules (serveur)',
  financements: 'schedules versionnés par contrat (serveur)',
  retenues: 'withholding_certificates (serveur)',
};

const Bandeau = ({ categorie }) => (
  <p className="mb-3 border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
    Source de vérité : <strong>{SOURCES[categorie]}</strong>. La retenue à la source reste séparée du coût de la facture et de son
    paiement bancaire ; le CMP d’achat pur n’est jamais modifié par ces opérations.
  </p>
);

export const DepensesCanoniques = ({ du, au, tiers = [] }) => {
  const [data, setData] = useState({ lignes: [], totaux: {}, avances_non_affectees: [] });
  const [form, setForm] = useState({ tiers_id: '', numero: '', date_piece: new Date().toISOString().slice(0, 10), montant_ttc: '', montant_ht: '', montant_tva: '', date_echeance: '' });
  const [ouvert, setOuvert] = useState(false);

  const charger = useCallback(() => {
    fetchDepensesCanoniques(du, au).then(setData);
  }, [du, au]);
  useEffect(() => {
    charger();
  }, [charger]);

  const enregistrer = async () => {
    if (!form.tiers_id || !form.numero || !form.montant_ttc) return toast.error('Tiers, numéro de facture et montant TTC sont obligatoires.');
    const { error, data: res } = await saisirFactureCanonique({
      ...form,
      montant_ttc: Number(form.montant_ttc),
      montant_ht: form.montant_ht ? Number(form.montant_ht) : null,
      montant_tva: form.montant_tva ? Number(form.montant_tva) : null,
      date_echeance: form.date_echeance || null,
      tiers_nom: tiers.find((t) => t.id === form.tiers_id)?.nom ?? null,
    });
    if (error) return toast.error(error);
    toast.success(res.cree ? 'Facture enregistrée dans la source canonique.' : res.message ?? 'Facture déjà connue : une seule dette conservée.');
    setOuvert(false);
    charger();
  };

  return (
    <Carte
      testid="canonique-depenses"
      titre="Dépenses & frais généraux — source canonique"
      sous="Une facture déposée dans Drive et la même facture saisie ici restent UNE SEULE dette (identité canonique commune)."
      action={
        <div className="flex gap-2">
          <button data-testid="canonique-depenses-nouvelle" type="button" onClick={() => setOuvert(!ouvert)} className={btnLight}>
            <Plus size={13} /> Saisir une facture
          </button>
          <button data-testid="canonique-depenses-recharger" type="button" onClick={charger} className={btnLight}>
            <RefreshCw size={13} /> Actualiser
          </button>
        </div>
      }
    >
      <Bandeau categorie="depenses" />

      {ouvert && (
        <div data-testid="canonique-depenses-form" className="mb-4 grid gap-3 border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3 lg:grid-cols-4">
          <label className="block">
            <span className={labelCls}>Fournisseur</span>
            <select data-testid="canonique-facture-tiers" value={form.tiers_id} onChange={(e) => setForm({ ...form, tiers_id: e.target.value })} className={inputCls}>
              <option value="">— Choisir —</option>
              {tiers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nom}
                </option>
              ))}
            </select>
          </label>
          {[
            ['numero', 'N° de facture', 'text'],
            ['date_piece', 'Date de la pièce', 'date'],
            ['date_echeance', 'Échéance', 'date'],
            ['montant_ht', 'Montant HT', 'number'],
            ['montant_tva', 'TVA', 'number'],
            ['montant_ttc', 'Montant TTC', 'number'],
          ].map(([k, l, type]) => (
            <label key={k} className="block">
              <span className={labelCls}>{l}</span>
              <input
                data-testid={`canonique-facture-${k}`}
                type={type}
                step={type === 'number' ? '0.001' : undefined}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={inputCls}
              />
            </label>
          ))}
          <div className="flex items-end">
            <button data-testid="canonique-facture-enregistrer" type="button" onClick={enregistrer} className={btn}>
              Enregistrer dans le canonique
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3">
        {[
          ['Total TTC', `${fmt(data.totaux?.montantTTC ?? 0, 3)} TND`, ''],
          ['Reste à payer', `${fmt(data.totaux?.reste ?? 0, 3)} TND`, DEFAVORABLE],
          ['Retenue à la source (séparée du coût)', `${fmt(data.totaux?.retenueSource ?? 0, 3)} TND`, NEUTRE_DOUX],
        ].map(([k, v, cls]) => (
          <div key={k} className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">{k}</p>
            <p className={`font-data text-base font-semibold ${cls}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[1040px] border-collapse">
          <thead className="bg-slate-50">
            <tr>
              {['Date', 'Fournisseur', 'N° facture', 'HT', 'TVA', 'TTC', 'RS', 'Réglé', 'Reste', 'État', 'Justif.'].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.lignes.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={11}>
                  Aucune dépense dans la source canonique pour cette période.
                </td>
              </tr>
            )}
            {data.lignes.map((l) => (
              <tr key={l.id} data-testid={`canonique-depense-${l.id}`}>
                <td className={td}>{l.date ?? '—'}</td>
                <td className={td}>{l.tiersNom ?? l.tiersId}</td>
                <td className={td}>{l.numeroFacture ?? '—'}</td>
                <td className={td}>{l.montantHT != null ? fmt(l.montantHT, 3) : '—'}</td>
                <td className={td}>{l.montantTVA != null ? fmt(l.montantTVA, 3) : '—'}</td>
                <td className={`${td} font-semibold`}>{l.montantTTC != null ? fmt(l.montantTTC, 3) : '—'}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{l.retenueSource ? `${fmt(l.retenueSource, 3)}${l.retenueSourceConfirmee ? ' ✓' : ''}` : '—'}</td>
                <td className={`${td} ${FAVORABLE}`}>{fmt(l.regle ?? 0, 3)}</td>
                <td className={`${td} ${DEFAVORABLE}`}>{fmt(l.reste ?? 0, 3)}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{l.etat}</td>
                <td className={td}>{l.justificatifs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.avances_non_affectees?.length > 0 && (
        <div className="mt-3 border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-900">Décaissements non affectés (toujours visibles, jamais masqués)</p>
          <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
            {data.avances_non_affectees.map((a) => (
              <li key={a.payment_id} data-testid={`avance-non-affectee-${a.payment_id}`}>
                {a.date ?? '—'} · {a.beneficiaire ?? a.party_id ?? 'tiers inconnu'} · {fmt(a.non_affecte_tnd ?? 0, 3)} TND non affectés
              </li>
            ))}
          </ul>
        </div>
      )}
    </Carte>
  );
};

export const FournisseursCanoniques = ({ tiers = [] }) => {
  const [data, setData] = useState({ comptes: [] });
  const [cible, setCible] = useState(null);
  const [form, setForm] = useState({ montant: '', date: new Date().toISOString().slice(0, 10), mode: 'Espèces', reference: '' });

  const charger = useCallback(() => fetchFournisseursCanoniques().then(setData), []);
  useEffect(() => {
    charger();
  }, [charger]);

  const regler = async () => {
    const { error, data: res } = await enregistrerReglementCanonique({
      party_id: cible.party_id,
      montant: Number(form.montant),
      date: form.date,
      mode: form.mode,
      reference: form.reference || null,
      beneficiaire: cible.nom ?? null,
    });
    if (error) return toast.error(error);
    toast.success(
      `Règlement enregistré : ${res.affectations.length} dette(s) mise(s) à jour${res.avance_non_affectee_tnd ? `, ${fmt(res.avance_non_affectee_tnd, 3)} TND en avance non affectée` : ''}.`
    );
    setCible(null);
    charger();
  };

  return (
    <Carte
      testid="canonique-fournisseurs"
      titre="Comptes fournisseurs & avances — source canonique"
      sous="Un paiement rapproché met à jour le solde fournisseur et la banque : une seule écriture, deux vues."
      action={
        <button data-testid="canonique-fournisseurs-recharger" type="button" onClick={charger} className={btnLight}>
          <RefreshCw size={13} /> Actualiser
        </button>
      }
    >
      <Bandeau categorie="fournisseurs" />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse">
          <thead className="bg-slate-50">
            <tr>
              {['Fournisseur', 'Dettes', 'Dette totale', 'Réglé', 'RS confirmée', 'Avance non affectée', 'Solde', 'Action'].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.comptes.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={8}>
                  Aucun compte fournisseur canonique.
                </td>
              </tr>
            )}
            {data.comptes.map((c) => (
              <tr key={c.party_id} data-testid={`canonique-fournisseur-${c.party_id}`}>
                <td className={td}>{c.nom ?? tiers.find((t) => t.id === c.party_id)?.nom ?? c.party_id}</td>
                <td className={td}>{c.obligations}</td>
                <td className={td}>{fmt(c.dette_tnd ?? 0, 3)}</td>
                <td className={`${td} ${FAVORABLE}`}>{fmt(c.regle_tnd ?? 0, 3)}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{fmt(c.rs_confirmee_tnd ?? 0, 3)}</td>
                <td className={td}>{fmt(c.avance_non_affectee_tnd ?? 0, 3)}</td>
                <td className={`${td} font-semibold ${(c.solde_tnd ?? 0) > 0 ? DEFAVORABLE : FAVORABLE}`}>{fmt(c.solde_tnd ?? 0, 3)}</td>
                <td className={td}>
                  <button data-testid={`regler-${c.party_id}`} type="button" onClick={() => setCible(c)} className={btnLight}>
                    Régler / Avance
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cible && (
        <div data-testid="canonique-reglement-form" className="mt-3 grid gap-3 border border-slate-200 bg-slate-50 p-3 sm:grid-cols-5">
          <p className="sm:col-span-5 text-xs text-slate-600">
            Règlement de <strong>{cible.nom ?? cible.party_id}</strong> — reste dû {fmt(cible.reste_tnd ?? 0, 3)} TND. Sans dette
            désignée, le surplus reste une avance visible dans les décaissements.
          </p>
          {[
            ['montant', 'Montant (TND)', 'number'],
            ['date', 'Date', 'date'],
            ['reference', 'N° de pièce', 'text'],
          ].map(([k, l, type]) => (
            <label key={k} className="block">
              <span className={labelCls}>{l}</span>
              <input
                data-testid={`reglement-${k}`}
                type={type}
                step={type === 'number' ? '0.001' : undefined}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={inputCls}
              />
            </label>
          ))}
          <label className="block">
            <span className={labelCls}>Mode</span>
            <select data-testid="reglement-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={inputCls}>
              {['Espèces', 'Chèque', 'Traite', 'Virement'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end gap-2">
            <button data-testid="reglement-valider" type="button" onClick={regler} className={btn}>
              Valider
            </button>
            <button type="button" onClick={() => setCible(null)} className={btnLight}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </Carte>
  );
};

export const BanqueCanonique = () => {
  const [data, setData] = useState({ transactions: [], extraits: [], synthese: {} });
  const charger = useCallback(() => fetchBanqueCanonique().then(setData), []);
  useEffect(() => {
    charger();
  }, [charger]);

  return (
    <Carte
      testid="canonique-banque"
      titre="Banque & caisse — source canonique"
      sous="Extraits importés par la passerelle, transactions canoniques et affectations validées."
      action={
        <button data-testid="canonique-banque-recharger" type="button" onClick={charger} className={btnLight}>
          <RefreshCw size={13} /> Actualiser
        </button>
      }
    >
      <Bandeau categorie="banque" />
      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ['Transactions', String(data.synthese?.transactions ?? 0)],
          ['Rapprochées', String(data.synthese?.rapprochees ?? 0)],
          ['À rapprocher', String(data.synthese?.a_rapprocher ?? 0)],
          ['Débits', `${fmt(data.synthese?.debits_tnd ?? 0, 3)} TND`],
          ['Crédits', `${fmt(data.synthese?.credits_tnd ?? 0, 3)} TND`],
        ].map(([k, v]) => (
          <div key={k} className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">{k}</p>
            <p className="font-data text-base font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 max-h-80 overflow-auto border border-slate-200">
        <table className="w-full min-w-[900px] border-collapse">
          <thead className="sticky top-0 bg-slate-50">
            <tr>
              {['Date', 'Compte', 'Libellé', 'Sens', 'Montant', 'Référence', 'Rapproché'].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.transactions.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={7}>
                  Aucune transaction bancaire canonique (importez un extrait dans la passerelle).
                </td>
              </tr>
            )}
            {data.transactions.map((t) => (
              <tr key={t.id} data-testid={`canonique-tx-${t.id}`}>
                <td className={td}>{t.date ?? '—'}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{t.compte_id}</td>
                <td className={td}>{t.libelle ?? '—'}</td>
                <td className={td}>{t.sens}</td>
                <td className={`${td} font-semibold ${t.sens === 'debit' ? DEFAVORABLE : FAVORABLE}`}>{fmt(t.montant_tnd ?? 0, 3)}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{t.reference_bancaire ?? '—'}</td>
                <td className={`${td} ${t.rapproche ? FAVORABLE : NEUTRE_DOUX}`}>{t.rapproche ? `Oui · ${t.payment_id}` : 'Non'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Carte>
  );
};

export const PrevisionnelCanonique = () => {
  const [data, setData] = useState({ lignes: [], decaissements_non_affectes: [] });
  const [financements, setFinancements] = useState({ contrats: [] });
  const charger = useCallback(() => {
    fetchPrevisionnelCanonique().then(setData);
    fetchFinancementsCanoniques().then(setFinancements);
  }, []);
  useEffect(() => {
    charger();
  }, [charger]);

  const doublons = useMemo(() => {
    // Un doublon réel = deux lignes désignant le MÊME objet (dette ou échéance de
    // contrat). Deux échéances distinctes de même montant et même date ne sont pas
    // un doublon : le compteur ne doit pas produire de fausse alerte.
    const vus = new Set();
    return data.lignes.filter((l) => {
      const cle = l.type === 'echeance_financement' ? `${l.contrat_id}|${l.numero}` : `obligation|${l.id}`;
      if (vus.has(cle)) return true;
      vus.add(cle);
      return false;
    }).length;
  }, [data.lignes]);

  return (
    <div className="space-y-4">
      <Carte
        testid="canonique-previsionnel"
        titre="Trésorerie prévisionnelle — source canonique"
        sous="Chaque échéance apparaît une seule fois : une dette portant un échéancier est représentée par ses échéances, jamais en double."
        action={
          <button data-testid="canonique-previsionnel-recharger" type="button" onClick={charger} className={btnLight}>
            <RefreshCw size={13} /> Actualiser
          </button>
        }
      >
        <Bandeau categorie="previsionnel" />
        <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3">
          {[
            ['Échéances à venir', String(data.lignes.length)],
            ['Total prévisionnel', `${fmt(data.total_tnd ?? 0, 3)} TND`],
            ['Avances non affectées', `${fmt(data.total_non_affecte_tnd ?? 0, 3)} TND`],
          ].map(([k, v]) => (
            <div key={k} className="bg-white px-3 py-2.5">
              <p className="text-[11px] text-slate-500">{k}</p>
              <p className="font-data text-base font-semibold text-slate-900">{v}</p>
            </div>
          ))}
        </div>
        <p data-testid="previsionnel-controle-doublon" className={`mt-2 text-[11px] ${doublons ? DEFAVORABLE : FAVORABLE}`}>
          Contrôle anti-double échéance : {doublons === 0 ? 'aucune échéance en doublon détectée.' : `${doublons} doublon(s) apparent(s) à vérifier.`}
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                {['Échéance', 'Type', 'Nature', 'Tiers / contrat', 'Montant', 'État'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.lignes.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={6}>
                    Aucune échéance canonique.
                  </td>
                </tr>
              )}
              {data.lignes.map((l) => (
                <tr key={l.id} data-testid={`previsionnel-${l.id}`}>
                  <td className={td}>{l.echeance ?? '—'}</td>
                  <td className={td}>{l.type === 'echeance_financement' ? 'Échéance de financement' : 'Dette'}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{l.nature}</td>
                  <td className={td}>{l.tiersNom ?? l.contrat_id ?? '—'}</td>
                  <td className={`${td} font-semibold ${DEFAVORABLE}`}>{fmt(l.montant_tnd ?? 0, 3)}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{l.etat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>

      <Carte testid="canonique-financements" titre="Financements & échéanciers — source canonique" sous="Un échéancier révisé est une nouvelle version du même contrat : l'historique est conservé, les échéances ne sont jamais ajoutées deux fois.">
        <Bandeau categorie="financements" />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                {['Contrat', 'Version', 'Échéances', 'Versions antérieures', 'Total', 'Restant'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {financements.contrats.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={6}>
                    Aucun contrat canonique.
                  </td>
                </tr>
              )}
              {financements.contrats.map((c) => (
                <tr key={c.contrat_id} data-testid={`financement-${c.contrat_id}`}>
                  <td className={td}>{c.contrat_id}</td>
                  <td className={td}>v{c.version}</td>
                  <td className={td}>{c.echeances.length}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{c.versions_anterieures}</td>
                  <td className={td}>{fmt(c.total_tnd ?? 0, 3)}</td>
                  <td className={`${td} font-semibold ${DEFAVORABLE}`}>{fmt(c.restant_tnd ?? 0, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
    </div>
  );
};

export const RetenuesCanoniques = () => {
  const [data, setData] = useState({ certificats: [] });
  useEffect(() => {
    fetchRetenuesCanoniques().then(setData);
  }, []);
  return (
    <Carte testid="canonique-retenues" titre="Retenues à la source — objets autonomes" sous="La RS est distincte de la facture, de son paiement bancaire et de son reversement à l'État.">
      <Bandeau categorie="retenues" />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse">
          <thead className="bg-slate-50">
            <tr>
              {['Référence', 'Émetteur', 'Date', 'Montant retenu', 'Affecté', 'Factures', 'État'].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.certificats.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={7}>
                  Aucune retenue à la source enregistrée.
                </td>
              </tr>
            )}
            {data.certificats.map((c) => (
              <tr key={c.id} data-testid={`rs-${c.id}`}>
                <td className={td}>{c.reference ?? '—'}</td>
                <td className={td}>{c.emetteur_party_id ?? '—'}</td>
                <td className={td}>{c.date ?? '—'}</td>
                <td className={`${td} font-semibold`}>{fmt(c.montant_tnd ?? 0, 3)}</td>
                <td className={td}>{fmt(c.affecte_tnd ?? 0, 3)}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{(c.documents ?? []).length}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{c.etat}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Carte>
  );
};
